<template>
  <v-main>
    <div class="certificate-page">
      <div class="certificate-container" id="certificate">
        <!-- Template Background -->
        <img :src="templateImage" alt="Certificate Template" class="certificate-template" />

        <!-- Text Overlay -->
        <div class="text-overlay">
          <!-- Title -->
          <h1 class="certificate-title">THIS CERTIFICATE IS</h1>

          <!-- Main Content Container (Presented to -> Course on) -->
          <div class="main-content">
            <p class="presented-to">Presented to</p>
            <h2 class="recipient-name">{{ certificateData.candidateName }}</h2>
            <p class="certificate-details">
              CDC number : {{ certificateData.cdcNumber }} for completion of {{ certificateData.courseName }}
            </p>
            <p class="certificate-details">
              course on {{ certificateData.completionDate }}.
            </p>
          </div>

          <!-- Validity Section (Bottom Left) -->
          <div class="validity-section">
            <p class="validity-text">
              This certificate is valid for a period of <span class="underline">{{ certificateData.validityPeriod }}</span>
            </p>
            <p class="validity-text">from the date of issue.</p>
          </div>

          <!-- Date Section (Bottom Right) -->
          <div class="date-section">
            <p class="date-value">{{ certificateData.presentedDate }}</p>
            <div class="date-line"></div>
            <p class="date-label">Date Presented</p>
          </div>
        </div>
      </div>

      <!-- Print/Download Button -->
      <div class="actions-section">
        <v-btn color="primary" @click="printCertificate" class="mr-4">
          <v-icon left>mdi-printer</v-icon>
          Print Certificate
        </v-btn>
        <v-btn color="success" @click="downloadPDF" :loading="isGenerating" :disabled="isGenerating">
          <v-icon left>mdi-download</v-icon>
          {{ isGenerating ? 'Generating...' : 'Download PDF' }}
        </v-btn>
      </div>
    </div>
  </v-main>
</template>

<script>
import templateImage from '@/assets/certificate-template.png';
import { generateCertificatePDF } from "@/services/certificatePdfService";

export default {
  name: "CertificateExample",
  data() {
    return {
      templateImage,
      isGenerating: false,
      certificateData: {
        candidateName: "Yujyait vig",
        cdcNumber: "xxxx",
        courseName: "xxxxxx",
        completionDate: "8th November, 2025",
        presentedDate: "8th november 2025",
        validityPeriod: "12 months"
      }
    };
  },
  methods: {
    printCertificate() {
      window.print();
    },
    async downloadPDF() {
      if (this.isGenerating) return;

      this.isGenerating = true;

      try {
        // Use the certificate PDF service
        await generateCertificatePDF(this.certificateData);
      } catch (error) {
        console.error("Error generating PDF:", error);
        alert("Error generating PDF. Please try again.");
      } finally {
        this.isGenerating = false;
      }
    }
  }
};
</script>

<style scoped>
/* Fonts loaded from local /fonts/fonts.css in index.html */

.certificate-page {
  padding: 40px 20px;
  background-color: #e8e8e8;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.certificate-container {
  position: relative;
  width: 900px;
  box-shadow: 0 15px 50px rgba(0,0,0,0.25);
}

.certificate-template {
  width: 100%;
  height: auto;
  display: block;
}

.text-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

/* Title - positioned at top center */
.certificate-title {
  position: absolute;
  top: 19%;
  left: 50%;
  transform: translateX(-50%);
  font-family: 'Playfair Display', serif;
  font-size: 26px;
  font-weight: 500;
  color: #1a4a6e;
  letter-spacing: 3px;
  text-align: center;
  margin: 0;
  white-space: nowrap;
}

/* Main Content Container - adjust top value to move entire block */
.main-content {
  position: absolute;
  top: 37%;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
}

.presented-to {
  font-family: 'Cormorant Garamond', serif;
  font-size: 20px;
  font-style: italic;
  color: #1a4a6e;
  margin: 0 0 5px 0;
}

.recipient-name {
  font-family: 'Great Vibes', cursive;
  font-size: 52px;
  color: #1a4a6e;
  margin: 0 0 10px 0;
  font-weight: 400;
  line-height: 1;
  white-space: nowrap;
}

.certificate-details {
  font-family: 'Cormorant Garamond', serif;
  font-size: 18px;
  color: #1a4a6e;
  margin: 3px 0;
  font-style: italic;
  font-weight: 500;
  white-space: nowrap;
}

/* Validity Section - Bottom Left */
.validity-section {
  position: absolute;
  bottom: 14%;
  left: 10%;
  text-align: left;
  max-width: 280px;
}

.validity-text {
  font-family: 'Cormorant Garamond', serif;
  font-size: 13px;
  font-style: italic;
  color: #1a4a6e;
  margin: 1px 0;
  line-height: 1.3;
}

.underline {
  text-decoration: underline;
  font-weight: 600;
}

/* Date Section - Bottom Right */
.date-section {
  position: absolute;
  bottom: 14%;
  right: 10%;
  text-align: center;
}

.date-value {
  font-family: 'Cormorant Garamond', serif;
  font-size: 14px;
  color: #1a4a6e;
  margin: 0 0 3px 0;
}

.date-line {
  width: 120px;
  height: 1px;
  background-color: #1a4a6e;
  margin: 0 auto 3px auto;
}

.date-label {
  font-family: 'Cormorant Garamond', serif;
  font-size: 12px;
  color: #1a4a6e;
  font-style: italic;
  margin: 0;
}

/* Actions */
.actions-section {
  margin-top: 30px;
  display: flex;
  justify-content: center;
}

/* Print Styles */
@media print {
  .actions-section {
    display: none !important;
  }

  .certificate-page {
    padding: 0;
    background: white;
    min-height: auto;
  }

  .certificate-container {
    box-shadow: none;
    width: 100%;
    max-width: 100%;
  }

  .v-main {
    padding: 0 !important;
  }
}
</style>
